import { useEffect, useState } from 'react'
import { fetchBrief, saveBrief, GATES, type Brief, type GateVerdict } from '../../lib/api'

// The five-gate Brief (Ember, task-008, 2026-07-12).
//
// Design constraint, learned the hard way from the record: on May 15 the LOB
// Spark asserted "EDGE: Speed. Sub-hour delivery." Flux — the delivery lead —
// refuted it in the thread (LOB took 2+ hours, not 15 minutes). Cadence and Relay
// concurred. Vector replied "Flux is right" and conceded the claim. And the
// Spark's summary line still read: "ready for Joel Review. Five seconds."
//
// A checkbox per gate would reproduce that failure exactly — you tick five boxes
// and the Brief looks green. So every gate here carries a VERDICT and its
// EVIDENCE, a gate may FAIL, and a Brief with a failing gate still submits. The
// failure travels WITH the idea instead of being smoothed out of the summary.
// A verdict with no evidence is an opinion, and the server rejects it.

const VERDICTS: { key: GateVerdict; label: string; color: string; hint: string }[] = [
  { key: 'pass', label: 'Pass', color: '#5AC884', hint: 'Evidence supports it' },
  { key: 'fail', label: 'Fail', color: '#D65A5A', hint: 'Evidence refutes it' },
  { key: 'unproven', label: 'Unproven', color: '#E6C85A', hint: 'No evidence either way — say so' },
]

const GATE_QUESTION: Record<string, string> = {
  Demand: 'Do people actually want this? Is there evidence?',
  Timing: 'Is this ready now, or are we too early?',
  Fit: 'Can our team actually execute it?',
  Edge: 'Do we have any advantage? Why us?',
  Autonomy: 'Can personas run this without constant human input?',
}

function emptyBrief(): Brief {
  return {
    what: '',
    gates: Object.fromEntries(GATES.map(g => [g, { verdict: 'unproven' as GateVerdict, evidence: '' }])),
    ledger_sort: '',
    build_shape: '',
    kill_criteria: '',
  }
}

export default function BriefComposer({ postId, title, onClose, onSaved }: {
  postId: string
  title: string
  onClose: () => void
  onSaved: () => void
}) {
  const [brief, setBrief] = useState<Brief>(emptyBrief())
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Revising an existing Brief is the normal case after a Yellow from Joel.
  useEffect(() => {
    fetchBrief(postId)
      .then(r => setBrief({ ...emptyBrief(), ...r.brief }))
      .catch(() => { /* no brief yet — start blank */ })
  }, [postId])

  const failed = GATES.filter(g => brief.gates[g]?.verdict === 'fail')
  const unproven = GATES.filter(g => brief.gates[g]?.verdict === 'unproven')

  async function save() {
    setSaving(true); setError(null)
    try {
      await saveBrief(postId, brief)
      onSaved()
    } catch (e) {
      let msg = e instanceof Error ? e.message : 'failed to save'
      try { msg = JSON.parse(msg).error || msg } catch { /* plain */ }
      setError(msg)
    }
    setSaving(false)
  }

  return (
    <div
      onClick={onClose}
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.62)', zIndex: 900, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '40px 20px', overflowY: 'auto' }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{ background: 'var(--bg-base)', border: '1px solid var(--border-subtle, rgba(255,255,255,0.12))', borderRadius: 14, padding: 26, maxWidth: 720, width: '100%' }}
      >
        <h2 style={{ fontFamily: 'Playfair Display, Georgia, serif', fontSize: 21, fontWeight: 700, marginBottom: 4 }}>Write the Brief</h2>
        <p style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 22 }}>{title}</p>

        <Field label="What it is" hint="One clear paragraph. What are we actually selling or building?"
          value={brief.what} onChange={v => setBrief({ ...brief, what: v })} rows={3} />

        {/* The five gates */}
        <div style={{ margin: '22px 0 6px' }}>
          <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 3 }}>The Five Gates</div>
          <p style={{ fontSize: 11.5, color: 'var(--text-muted)', lineHeight: 1.5, marginBottom: 14 }}>
            Score each gate honestly. <strong>A gate is allowed to fail</strong> — a Brief with a failing gate still goes forward, and the failure travels with it. What is not allowed is a verdict with no evidence.
          </p>
        </div>

        {GATES.map(gate => {
          const entry = brief.gates[gate] || { verdict: 'unproven' as GateVerdict, evidence: '' }
          const active = VERDICTS.find(v => v.key === entry.verdict)!
          return (
            <div key={gate} style={{ marginBottom: 14, padding: 12, borderRadius: 9, background: 'var(--bg-elevated, rgba(255,255,255,0.03))', borderLeft: `3px solid ${active.color}` }}>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap', marginBottom: 8 }}>
                <span style={{ fontWeight: 700, fontSize: 13 }}>{gate}</span>
                <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>{GATE_QUESTION[gate]}</span>
              </div>

              <div style={{ display: 'flex', gap: 6, marginBottom: 8 }}>
                {VERDICTS.map(v => (
                  <button
                    key={v.key}
                    title={v.hint}
                    onClick={() => setBrief({ ...brief, gates: { ...brief.gates, [gate]: { ...entry, verdict: v.key } } })}
                    style={{
                      padding: '4px 12px', fontSize: 11, fontWeight: 600, borderRadius: 6, cursor: 'pointer', fontFamily: 'inherit',
                      background: entry.verdict === v.key ? `${v.color}28` : 'transparent',
                      color: entry.verdict === v.key ? v.color : 'var(--text-muted)',
                      border: `1px solid ${entry.verdict === v.key ? `${v.color}88` : 'var(--border-subtle, rgba(255,255,255,0.12))'}`,
                    }}
                  >
                    {v.label}
                  </button>
                ))}
              </div>

              <textarea
                value={entry.evidence}
                onChange={e => setBrief({ ...brief, gates: { ...brief.gates, [gate]: { ...entry, evidence: e.target.value } } })}
                placeholder={entry.verdict === 'fail'
                  ? 'What refutes it, and who said so? Name them.'
                  : entry.verdict === 'unproven'
                    ? 'What would it take to know? Say plainly that we do not.'
                    : 'What is the evidence? Numbers, names, shipped work.'}
                rows={2}
                style={inputStyle}
              />
            </div>
          )
        })}

        <Field label="Ledger sort" hint="Labor-rich, attention-rich, capital-constrained? Services-that-productize beat capital-intensive plays."
          value={brief.ledger_sort || ''} onChange={v => setBrief({ ...brief, ledger_sort: v })} rows={2} />

        <Field label="Shape of the build" hint="Who does what, over what horizon, at what price. Bounded and action-shaped — not research-shaped."
          value={brief.build_shape} onChange={v => setBrief({ ...brief, build_shape: v })} rows={3} />

        <Field label="Kill criteria" hint="What would make this a bad idea? If you cannot name it, you have not thought about it enough."
          value={brief.kill_criteria} onChange={v => setBrief({ ...brief, kill_criteria: v })} rows={2} />

        {/* Honest scoreboard — never hide a failing gate behind a green count. */}
        <div style={{ margin: '18px 0', padding: '11px 14px', borderRadius: 9, fontSize: 12, lineHeight: 1.6, background: failed.length ? 'rgba(214,90,90,0.10)' : 'var(--bg-elevated, rgba(255,255,255,0.03))', border: `1px solid ${failed.length ? 'rgba(214,90,90,0.4)' : 'var(--border-subtle, rgba(255,255,255,0.08))'}` }}>
          <strong>{GATES.length - failed.length - unproven.length} of 5 gates pass.</strong>
          {failed.length > 0 && <> <span style={{ color: '#E68A8A' }}>Failing: {failed.join(', ')}.</span></>}
          {unproven.length > 0 && <> <span style={{ color: '#E6C85A' }}>Unproven: {unproven.join(', ')}.</span></>}
          {failed.length > 0 && (
            <div style={{ marginTop: 5, color: 'var(--text-muted)', fontSize: 11.5 }}>
              This Brief can still go to Joel. The failing gate goes with it — that is the point.
            </div>
          )}
        </div>

        {error && <div style={{ color: '#E68A8A', fontSize: 12, marginBottom: 12 }}>{error}</div>}

        <div style={{ display: 'flex', gap: 9, justifyContent: 'flex-end' }}>
          <button onClick={onClose} style={{ ...btnStyle, color: 'var(--text-muted)', border: '1px solid var(--border-subtle, rgba(255,255,255,0.12))', background: 'transparent' }}>Cancel</button>
          <button onClick={save} disabled={saving} style={{ ...btnStyle, color: '#fff', background: '#6FB8D6', border: 'none', opacity: saving ? 0.6 : 1 }}>
            {saving ? 'Saving…' : 'Save Brief'}
          </button>
        </div>
      </div>
    </div>
  )
}

function Field({ label, hint, value, onChange, rows }: { label: string; hint: string; value: string; onChange: (v: string) => void; rows: number }) {
  return (
    <div style={{ marginBottom: 15 }}>
      <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 2 }}>{label}</div>
      <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 6, lineHeight: 1.45 }}>{hint}</div>
      <textarea value={value} onChange={e => onChange(e.target.value)} rows={rows} style={inputStyle} />
    </div>
  )
}

const inputStyle: React.CSSProperties = {
  width: '100%', padding: '8px 10px', fontSize: 12.5, lineHeight: 1.5, borderRadius: 7,
  background: 'var(--bg-base)', color: 'var(--text-primary)',
  border: '1px solid var(--border-subtle, rgba(255,255,255,0.12))',
  fontFamily: 'inherit', resize: 'vertical', boxSizing: 'border-box',
}

const btnStyle: React.CSSProperties = {
  padding: '8px 18px', fontSize: 12, fontWeight: 600, borderRadius: 8, cursor: 'pointer', fontFamily: 'inherit',
}
